import { defineStore } from "pinia";
import type { TUser } from "@/interfaces/api";

export const LOADER = {
  NJORDA: "NJORDA",
  CIRCLE: "CIRCLE",
};

export type TLayoutSettings = {
  name: string;
  showLogo: boolean;
  showLogoTitle: boolean;
  fadeBackgroundImage: boolean;
  loaderImage: string;
  logoImage: string;
  logoImageVariant1: string;
  backgroundImage: string;
  showLanguageSwitcher: boolean;
  favicon: string;
  spinner: (typeof LOADER)[keyof typeof LOADER];
  showPeriodPicker: boolean; // is it ever used?
  isFeedbackDialogOpen: boolean;
};

type TAppStoreState = {
  _layoutSettings: TLayoutSettings;
  _user: TUser | null;
  _drawer: boolean;
};

const defaultLayoutSettings = {
  name: "Njorda B2B",
  showLogo: true,
  showLogoTitle: false,
  fadeBackgroundImage: true,
  loaderImage: "/loader.gif",
  logoImage: "/njorda_logo.svg",
  logoImageVariant1: "/njorda_logo.svg",
  backgroundImage: "/portfolio-fetcher@2x.svg",
  showLanguageSwitcher: true,
  favicon: "/favicon.png",
  spinner: LOADER.NJORDA,
  showPeriodPicker: true,
  isFeedbackDialogOpen: false,
};

export const useAppStore = defineStore("appStore", {
  state: () =>
    ({
      _layoutSettings: {
        ...defaultLayoutSettings,
      },
      _user: null,
      _drawer: true,
    }) as TAppStoreState,
  getters: {
    layoutSettings: (state) => state._layoutSettings,
    drawer: (state) => state._drawer,
    user: (state) => state._user,
    isFeedbackDialogOpen: (state) => state._layoutSettings.isFeedbackDialogOpen,
  },
  actions: {
    // Mutations
    updateLayoutSettings(layoutSettings: Partial<TLayoutSettings>) {
      this._layoutSettings = {
        ...this._layoutSettings,
        ...layoutSettings,
      };
    },
    resetLayoutSettings() {
      this._layoutSettings = {
        ...defaultLayoutSettings,
      };
    },
    setUser(user: TUser | null) {
      this._user = user;
    },
    setDrawer(drawer: boolean) {
      this._drawer = drawer;
    },
    async logout() {
      const $router = useRouter();
      const { $organization } = useNuxtApp();
      const org = $organization.getCurrent();
      $router.push({
        path: "/logout",
        query: {
          org_id: org?.public_id,
        },
      });
    },
  },
});

type UseNullStore = ReturnType<typeof defineStore>;
type NullStore = ReturnType<UseNullStore>;
type AppStoreNoSGA = ReturnType<typeof useAppStore>;
export type TAppStore = Omit<AppStoreNoSGA, keyof NullStore>;
